import { denormalisedResponseEntities, ensureOwnListing } from '../util/data';
import { needsEmailVerificationPrompt } from '../util/emailVerification';
import { storableError } from '../util/errors';
import {
  getLocalCartItems,
  getLocalCartQuantities,
  getLocalWishlist,
  setLocalWishlist,
} from '../util/guestStorage';
import { cartUnitCount } from '../util/cartRules';
import { queryListingsByIds } from '../util/listingsByIds';
import { withBusyCursor } from '../util/busyCursor';
import { LISTING_STATE_DRAFT } from '../util/types';
import * as log from '../util/log';

import { authInfo, mergeGuestDataIntoAccount } from './auth.duck';
import { stripeAccountCreateSuccess } from './stripeConnectAccount.duck';

// ================ Action types ================ //

export const CURRENT_USER_SHOW_REQUEST = 'app/user/CURRENT_USER_SHOW_REQUEST';
export const CURRENT_USER_SHOW_SUCCESS = 'app/user/CURRENT_USER_SHOW_SUCCESS';
export const CURRENT_USER_SHOW_ERROR = 'app/user/CURRENT_USER_SHOW_ERROR';

export const CLEAR_CURRENT_USER = 'app/user/CLEAR_CURRENT_USER';

export const FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST';
export const FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_LISTINGS_ERROR =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_ERROR';

export const FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST';
export const FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS';
export const FETCH_CURRENT_USER_NOTIFICATIONS_ERROR =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_ERROR';

export const FETCH_CURRENT_USER_HAS_ORDERS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_REQUEST';
export const FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_ORDERS_ERROR = 'app/user/FETCH_CURRENT_USER_HAS_ORDERS_ERROR';

export const FETCH_CURRENT_USER_CART_COUNT_REQUEST =
  'app/user/FETCH_CURRENT_USER_CART_COUNT_REQUEST';
export const FETCH_CURRENT_USER_CART_COUNT_SUCCESS =
  'app/user/FETCH_CURRENT_USER_CART_COUNT_SUCCESS';
export const FETCH_CURRENT_USER_CART_COUNT_ERROR = 'app/user/FETCH_CURRENT_USER_CART_COUNT_ERROR';

export const GUEST_WISHLIST_UPDATED = 'app/user/GUEST_WISHLIST_UPDATED';

export const WISHLIST_CHANGED = 'app/user/WISHLIST_CHANGED';
export const WISHLIST_SAVE_SUCCESS = 'app/user/WISHLIST_SAVE_SUCCESS';
export const WISHLIST_SAVE_ERROR = 'app/user/WISHLIST_SAVE_ERROR';

export const SEND_VERIFICATION_EMAIL_REQUEST = 'app/user/SEND_VERIFICATION_EMAIL_REQUEST';
export const SEND_VERIFICATION_EMAIL_SUCCESS = 'app/user/SEND_VERIFICATION_EMAIL_SUCCESS';
export const SEND_VERIFICATION_EMAIL_ERROR = 'app/user/SEND_VERIFICATION_EMAIL_ERROR';

// ================ Reducer ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
    ? newCurrentUser
    : { id, type, attributes, ...oldRelationships, ...relationships };
};

const initialState = {
  currentUser: null,
  currentUserShowError: null,
  currentUserHasListings: false,
  currentUserHasListingsError: null,
  currentUserNotificationCount: 0, // Provider notifications (sales)
  currentUserCustomerNotificationCount: 0, // Customer notifications (orders)
  currentUserNotificationCountError: null,
  // The latest notification count request; an older answer is dropped.
  notificationCountRequest: null,
  currentUserHasOrders: null, // This is not fetched unless unverified emails exist
  currentUserHasOrdersError: null,
  currentUserCartCount: 0,
  currentUserCartCountError: null,
  // The latest cart count request; an older answer is dropped.
  cartCountRequest: null,
  // A signed-in buyer's favorites while a change is being saved: the hearts
  // show it at once. Null when the profile holds what the buyer chose.
  wishlistPending: null,
  // Counts the buyer's favorite changes, so a save only clears wishlistPending
  // when no change came after it.
  wishlistChange: 0,
  wishlistSaveError: null,
  // Wishlist of a non-authenticated visitor, mirrored from localStorage so
  // hearts and the topbar badge re-render on toggle.
  guestWishlist: [],
  sendVerificationEmailInProgress: false,
  sendVerificationEmailError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CURRENT_USER_SHOW_REQUEST:
      return { ...state, currentUserShowError: null };
    case CURRENT_USER_SHOW_SUCCESS:
      return { ...state, currentUser: mergeCurrentUser(state.currentUser, payload) };
    case CURRENT_USER_SHOW_ERROR:
      log.error(payload, 'current-user-show-error');
      return { ...state, currentUserShowError: payload };

    case CLEAR_CURRENT_USER:
      return {
        ...state,
        currentUser: null,
        currentUserShowError: null,
        currentUserHasListings: false,
        currentUserHasListingsError: null,
        currentUserNotificationCount: 0,
        currentUserCustomerNotificationCount: 0,
        currentUserNotificationCountError: null,
        currentUserCartCount: 0,
        currentUserCartCountError: null,
        wishlistPending: null,
        wishlistSaveError: null,
      };

    case FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST:
      return { ...state, currentUserHasListingsError: null };
    case FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS:
      return { ...state, currentUserHasListings: payload.hasListings };
    case FETCH_CURRENT_USER_HAS_LISTINGS_ERROR:
      log.error(payload, 'fetch-current-user-has-listings-error');
      return { ...state, currentUserHasListingsError: payload };

    case FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST:
      return {
        ...state,
        notificationCountRequest: payload,
        currentUserNotificationCountError: null,
      };
    case FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS:
      return action.meta?.request !== state.notificationCountRequest
        ? state
        : {
            ...state,
            currentUserNotificationCount: payload.providerCount,
            currentUserCustomerNotificationCount: payload.customerCount,
          };
    case FETCH_CURRENT_USER_NOTIFICATIONS_ERROR:
      if (action.meta?.request !== state.notificationCountRequest) return state;
      log.error(payload, 'fetch-current-user-notifications-error');
      return { ...state, currentUserNotificationCountError: payload };

    case FETCH_CURRENT_USER_HAS_ORDERS_REQUEST:
      return { ...state, currentUserHasOrdersError: null };
    case FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS:
      return { ...state, currentUserHasOrders: payload.hasOrders };
    case FETCH_CURRENT_USER_HAS_ORDERS_ERROR:
      log.error(payload, 'fetch-current-user-has-orders-error');
      return { ...state, currentUserHasOrdersError: payload };

    case GUEST_WISHLIST_UPDATED:
      return { ...state, guestWishlist: payload };

    case WISHLIST_CHANGED:
      return {
        ...state,
        wishlistPending: payload,
        wishlistChange: state.wishlistChange + 1,
        wishlistSaveError: null,
      };
    case WISHLIST_SAVE_SUCCESS:
      return action.meta?.change !== state.wishlistChange
        ? state
        : { ...state, wishlistPending: null };
    case WISHLIST_SAVE_ERROR:
      // The hearts go back to what the profile holds.
      return { ...state, wishlistPending: null, wishlistSaveError: payload };

    case FETCH_CURRENT_USER_CART_COUNT_REQUEST:
      return { ...state, cartCountRequest: payload, currentUserCartCountError: null };
    case FETCH_CURRENT_USER_CART_COUNT_SUCCESS:
      return action.meta?.request !== state.cartCountRequest
        ? state
        : { ...state, currentUserCartCount: payload.cartCount };
    case FETCH_CURRENT_USER_CART_COUNT_ERROR:
      if (action.meta?.request !== state.cartCountRequest) return state;
      log.error(payload, 'fetch-current-user-cart-count-error');
      return { ...state, currentUserCartCountError: payload };

    case SEND_VERIFICATION_EMAIL_REQUEST:
      return {
        ...state,
        sendVerificationEmailInProgress: true,
        sendVerificationEmailError: null,
      };
    case SEND_VERIFICATION_EMAIL_SUCCESS:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
      };
    case SEND_VERIFICATION_EMAIL_ERROR:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
        sendVerificationEmailError: payload,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};

export const verificationSendingInProgress = state => {
  return state.user.sendVerificationEmailInProgress;
};

export const selectCurrentUser = state => {
  // Handle potential null/undefined state gracefully
  return state?.user?.currentUser ?? null;
};

// Wishlist listing ids for the current visitor: profile publicData for an
// authenticated user, the localStorage-mirrored guest wishlist otherwise.
// Returns a STABLE empty array when there is none — this selector feeds
// useSelector in every ListingCard on a search page, and a fresh [] per
// call would force all of them to re-render on every store dispatch.
const EMPTY_WISHLIST = [];
export const selectWishlistIds = state => {
  const { currentUser, guestWishlist, wishlistPending } = state?.user || {};
  return currentUser
    ? wishlistPending || currentUser.attributes?.profile?.publicData?.wishlist || EMPTY_WISHLIST
    : guestWishlist || EMPTY_WISHLIST;
};

// ================ Action creators ================ //

export const currentUserShowRequest = () => ({ type: CURRENT_USER_SHOW_REQUEST });

export const currentUserShowSuccess = user => ({
  type: CURRENT_USER_SHOW_SUCCESS,
  payload: user,
});

export const currentUserShowError = e => ({
  type: CURRENT_USER_SHOW_ERROR,
  payload: e,
  error: true,
});

export const clearCurrentUser = () => ({ type: CLEAR_CURRENT_USER });

const fetchCurrentUserHasListingsRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST,
});

export const fetchCurrentUserHasListingsSuccess = hasListings => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS,
  payload: { hasListings },
});

const fetchCurrentUserHasListingsError = e => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserNotificationsRequest = request => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST,
  payload: request,
});

// The unread counts: providerCount on the user's sales, customerCount on their orders
export const fetchCurrentUserNotificationsSuccess = (counts, request) => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS,
  payload: { providerCount: counts.providerCount, customerCount: counts.customerCount },
  meta: { request },
});

const fetchCurrentUserNotificationsError = (e, request) => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_ERROR,
  error: true,
  payload: e,
  meta: { request },
});

const fetchCurrentUserHasOrdersRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_REQUEST,
});

export const fetchCurrentUserHasOrdersSuccess = hasOrders => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS,
  payload: { hasOrders },
});

const fetchCurrentUserHasOrdersError = e => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_ERROR,
  error: true,
  payload: e,
});

export const fetchCurrentUserCartCountRequest = request => ({
  type: FETCH_CURRENT_USER_CART_COUNT_REQUEST,
  payload: request,
});

export const fetchCurrentUserCartCountSuccess = (cartCount, request) => ({
  type: FETCH_CURRENT_USER_CART_COUNT_SUCCESS,
  payload: { cartCount },
  meta: { request },
});

const fetchCurrentUserCartCountError = (e, request) => ({
  type: FETCH_CURRENT_USER_CART_COUNT_ERROR,
  error: true,
  payload: e,
  meta: { request },
});

const wishlistChanged = wishlist => ({ type: WISHLIST_CHANGED, payload: wishlist });
const wishlistSaveSuccess = change => ({ type: WISHLIST_SAVE_SUCCESS, meta: { change } });
const wishlistSaveError = e => ({ type: WISHLIST_SAVE_ERROR, error: true, payload: e });

export const guestWishlistUpdated = wishlist => ({
  type: GUEST_WISHLIST_UPDATED,
  payload: wishlist,
});

export const sendVerificationEmailRequest = () => ({
  type: SEND_VERIFICATION_EMAIL_REQUEST,
});

export const sendVerificationEmailSuccess = () => ({
  type: SEND_VERIFICATION_EMAIL_SUCCESS,
});

export const sendVerificationEmailError = e => ({
  type: SEND_VERIFICATION_EMAIL_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasListingsRequest());
  const { currentUser } = getState().user;

  if (!currentUser) {
    dispatch(fetchCurrentUserHasListingsSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    // Since we are only interested in if the user has
    // listings, we only need at most one result.
    page: 1,
    perPage: 1,
  };

  return sdk.ownListings
    .query(params)
    .then(response => {
      const hasListings = response.data.data && response.data.data.length > 0;

      const hasPublishedListings =
        hasListings &&
        ensureOwnListing(response.data.data[0]).attributes.state !== LISTING_STATE_DRAFT;
      dispatch(fetchCurrentUserHasListingsSuccess(!!hasPublishedListings));
    })
    .catch(e => dispatch(fetchCurrentUserHasListingsError(storableError(e))));
};

export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasOrdersRequest());

  if (!getState().user.currentUser) {
    dispatch(fetchCurrentUserHasOrdersSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      dispatch(fetchCurrentUserHasOrdersSuccess(!!hasOrders));
    })
    .catch(e => dispatch(fetchCurrentUserHasOrdersError(storableError(e))));
};

// Toggle a listing in the guest (localStorage) wishlist and mirror the
// result into redux so hearts and the topbar badge update immediately.
export const toggleGuestWishlistItem = listingId => dispatch => {
  const current = getLocalWishlist();
  const updated = current.includes(listingId)
    ? current.filter(id => id !== listingId)
    : [...current, listingId];
  setLocalWishlist(updated);
  dispatch(guestWishlistUpdated(updated));
  return Promise.resolve(updated);
};

/**
 * Saves profile fields of the signed-in user and puts the saved user in the store.
 * Rejects when the save fails, so the caller can show it and undo what it showed.
 */
export const saveCurrentUserProfile = params => (dispatch, getState, sdk) => {
  const queryParams = {
    expand: true,
    include: ['profileImage'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
  };
  return sdk.currentUser.updateProfile(params, queryParams).then(response => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
    }
    dispatch(currentUserShowSuccess(entities[0]));
    return entities[0];
  });
};

// Favorite saves run one at a time; each sends the buyer's latest list.
let wishlistWrites = Promise.resolve();

/**
 * Adds or removes a favorite. The hearts change at once. A signed-in user's list
 * is saved to the profile; if that fails the hearts go back to the saved list and
 * the thunk resolves false. Guests keep theirs in this browser.
 */
export const toggleWishlistItem = listingId => (dispatch, getState) => {
  if (!getState().auth?.isAuthenticated || !getState().user?.currentUser) {
    return dispatch(toggleGuestWishlistItem(listingId)).then(() => true);
  }
  const current = selectWishlistIds(getState());
  const updated = current.includes(listingId)
    ? current.filter(id => id !== listingId)
    : [...current, listingId];
  dispatch(wishlistChanged(updated));

  const save = () => {
    const { wishlistPending, wishlistChange } = getState().user;
    // An earlier save already sent this list, or a failure dropped it
    if (!wishlistPending) return Promise.resolve(!getState().user.wishlistSaveError);
    return withBusyCursor(
      dispatch(saveCurrentUserProfile({ publicData: { wishlist: wishlistPending } }))
    )
      .then(() => {
        dispatch(wishlistSaveSuccess(wishlistChange));
        return true;
      })
      .catch(e => {
        log.error(e, 'wishlist-save-failed');
        dispatch(wishlistSaveError(storableError(e)));
        return false;
      });
  };
  const run = wishlistWrites.then(save);
  wishlistWrites = run;
  return run;
};

// The topbar's cart count: the units of the cart's listings that can still be bought,
// leaving out the buyer's own, counted as the cart page counts them (util/cartRules).
let cartCountRequests = 0;
export const fetchCurrentUserCartCount = () => (dispatch, getState, sdk) => {
  cartCountRequests += 1;
  const request = cartCountRequests;
  dispatch(fetchCurrentUserCartCountRequest(request));

  const { currentUser } = getState().user || {};

  // Guests keep their cart in localStorage; authenticated carts live in
  // profile protectedData.
  const protectedData = currentUser?.attributes?.profile?.protectedData;
  const cartItems = currentUser ? protectedData?.cartItems || [] : getLocalCartItems();
  const quantities = currentUser ? protectedData?.cartQuantities || {} : getLocalCartQuantities();

  if (cartItems.length === 0) {
    dispatch(fetchCurrentUserCartCountSuccess(0, request));
    return Promise.resolve(null);
  }

  const params = {
    minStock: 1,
    include: ['author', 'currentStock'],
    'fields.listing': ['state'],
    'fields.user': ['profile.displayName'],
  };
  return queryListingsByIds(sdk, cartItems, params)
    .then(response => {
      const listings = denormalisedResponseEntities(response);
      const count = cartUnitCount(listings, quantities, currentUser?.id?.uuid);
      dispatch(fetchCurrentUserCartCountSuccess(count, request));
    })
    .catch(e => {
      dispatch(fetchCurrentUserCartCountError(storableError(e), request));
      // On error, fallback to cart items length
      dispatch(fetchCurrentUserCartCountSuccess(cartItems.length, request));
    });
};

// The most transactions one query page holds
const NOTIFICATION_PAGE_SIZE = 100;

// Sums one party's unread counts (metadata.notifications[party]) over every transaction of
// one kind ('sale' or 'order'), page by page. Only the metadata is asked for.
const sumUnreadNotifications = async (sdk, only, party) => {
  let total = 0;
  let page = 1;
  let totalPages = 1;
  do {
    const response = await sdk.transactions.query({
      only,
      page,
      perPage: NOTIFICATION_PAGE_SIZE,
      'fields.transaction': ['metadata'],
    });
    total += (response.data.data || []).reduce(
      (sum, tx) => sum + (tx?.attributes?.metadata?.notifications?.[party] || 0),
      0
    );
    totalPages = response.data.meta?.totalPages || 1;
    page += 1;
  } while (page <= totalPages);
  return total;
};

let notificationCountRequests = 0;
export const fetchCurrentUserNotifications = () => (dispatch, getState, sdk) => {
  notificationCountRequests += 1;
  const request = notificationCountRequests;
  dispatch(fetchCurrentUserNotificationsRequest(request));

  // The seller's unread count is on their sales, the buyer's on their orders
  return Promise.all([
    sumUnreadNotifications(sdk, 'sale', 'provider'),
    sumUnreadNotifications(sdk, 'order', 'customer'),
  ])
    .then(([providerCount, customerCount]) => {
      dispatch(fetchCurrentUserNotificationsSuccess({ providerCount, customerCount }, request));
    })
    .catch(e => dispatch(fetchCurrentUserNotificationsError(storableError(e), request)));
};

export const fetchCurrentUser = (params = null) => (dispatch, getState, sdk) => {
  dispatch(currentUserShowRequest());
  const { isAuthenticated } = getState().auth;

  if (!isAuthenticated) {
    // Make sure current user is null
    dispatch(currentUserShowSuccess(null));
    // Guests still get topbar cart/wishlist badges, sourced from localStorage
    dispatch(guestWishlistUpdated(getLocalWishlist()));
    dispatch(fetchCurrentUserCartCount());
    return Promise.resolve({});
  }

  const parameters = params || {
    include: ['profileImage', 'stripeAccount'],
  };

  return sdk.currentUser
    .show(parameters)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(stripeAccountCreateSuccess(currentUser.stripeAccount));
      }

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      dispatch(currentUserShowSuccess(currentUser));
      return currentUser;
    })
    .then(currentUser =>
      // Merge any guest (localStorage) cart/wishlist into the account first, so whatever
      // waits for the current user (the cart page) reads the merged cart.
      // This is the single choke point every auth flavor passes through.
      dispatch(mergeGuestDataIntoAccount(currentUser)).then(() => currentUser)
    )
    .then(currentUser => {
      dispatch(fetchCurrentUserHasListings());
      dispatch(fetchNotificationsIfNeeded()); // Fetch notifications (with 60s throttle)
      dispatch(fetchCurrentUserCartCount());
      if (needsEmailVerificationPrompt(currentUser)) {
        dispatch(fetchCurrentUserHasOrders());
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
      return currentUser;
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      dispatch(currentUserShowError(storableError(e)));
    });
};

export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  if (verificationSendingInProgress(getState())) {
    return Promise.reject(new Error('Verification email sending already in progress'));
  }
  dispatch(sendVerificationEmailRequest());
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => dispatch(sendVerificationEmailSuccess()))
    .catch(e => dispatch(sendVerificationEmailError(storableError(e))));
};

// ================ Notification Polling ================ //

// Module-level variable to track last notification fetch time
let lastNotificationFetchAt = null;

// Minimum interval between notification fetches (60 seconds)
const NOTIFICATION_FETCH_INTERVAL_MS = 60000;

/**
 * Fetch notifications if enough time has passed since last fetch
 * This prevents excessive API calls while ensuring updates are checked periodically
 */
export const fetchNotificationsIfNeeded = () => (dispatch, getState) => {
  const now = Date.now();
  const { currentUser } = getState().user;

  if (!currentUser) {
    return;
  }

  // Only fetch if more than 60 seconds have passed since last fetch
  if (!lastNotificationFetchAt || (now - lastNotificationFetchAt) >= NOTIFICATION_FETCH_INTERVAL_MS) {
    lastNotificationFetchAt = now;
    dispatch(fetchCurrentUserNotifications());
  }
};

/**
 * Reset notification fetch timestamp (called on logout)
 */
export const resetNotificationFetchTimestamp = () => {
  lastNotificationFetchAt = null;
};
